import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/bi/EmptyState";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2, Pencil, UserPlus, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

function GroupDialog({ open, onOpenChange, initial, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(initial?.name || ""); setDesc(initial?.description || ""); }, [initial, open]);
  const save = async () => {
    if (!name.trim()) { toast.error("Enter a group name"); return; }
    setBusy(true);
    try {
      const { data } = initial?.id
        ? await api.patch(`/access/groups/${initial.id}`, { name: name.trim(), description: desc })
        : await api.post("/access/groups", { name: name.trim(), description: desc });
      toast.success(initial?.id ? "Group updated" : `Group “${data.name}” created`);
      onSaved(data); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-display">{initial?.id ? "Rename group" : "New group"}</DialogTitle></DialogHeader>
        <div className="space-y-2 py-1">
          <Input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Finance Team" className="bg-[color:var(--surface)]" maxLength={80} />
          <Input data-testid="group-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" className="bg-[color:var(--surface)]" maxLength={280} />
        </div>
        <DialogFooter><Button data-testid="group-save-btn" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersPanel({ group, users, onChanged }) {
  const [pick, setPick] = useState("");
  const memberIds = new Set((group.members || []).map((m) => m.user_id));
  const candidates = (users || []).filter((u) => !memberIds.has(u.id));
  const add = async () => {
    if (!pick) return;
    try { const { data } = await api.post(`/access/groups/${group.id}/members`, { user_id: pick }); onChanged(data); setPick(""); toast.success("Member added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (uid) => {
    try { const { data } = await api.delete(`/access/groups/${group.id}/members/${uid}`); onChanged(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="group-members-panel" className="glass-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0"><h3 className="font-display text-base text-[color:var(--ink)] truncate">{group.name}</h3>{group.description && <p className="text-xs text-[color:var(--ink-3)] truncate">{group.description}</p>}</div>
        <Badge variant="outline" className="shrink-0">{(group.members || []).length} member{(group.members || []).length === 1 ? "" : "s"}</Badge>
      </div>
      <div className="flex gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger data-testid="group-member-pick" className="h-8 text-xs bg-[color:var(--surface)]"><SelectValue placeholder="Add a teammate…" /></SelectTrigger>
          <SelectContent>{candidates.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`group-member-opt-${u.id}`}>{u.name || u.email} <span className="text-[color:var(--ink-3)]">· {u.email}</span></SelectItem>)}
            {!candidates.length && <div className="px-2 py-1.5 text-xs text-[color:var(--ink-3)]">Everyone is already a member</div>}</SelectContent>
        </Select>
        <Button data-testid="group-member-add" size="sm" className="h-8 bg-[color:var(--brand-ocean)] text-white shrink-0" disabled={!pick} onClick={add}><UserPlus className="w-4 h-4" /></Button>
      </div>
      <div className="space-y-1">
        {(group.members || []).map((m) => (
          <div key={m.user_id} data-testid="group-member-row" className="flex items-center justify-between rounded-lg border border-[color:var(--glass-border)] px-3 py-1.5 text-xs">
            <span className="truncate text-[color:var(--ink-2)]">{m.name || m.email} <span className="text-[color:var(--ink-3)]">· {m.email}</span></span>
            <span className="flex items-center gap-2 shrink-0"><Badge variant="outline" className="text-[10px] capitalize">{m.role}</Badge>
              <button data-testid={`group-member-remove-${m.user_id}`} onClick={() => remove(m.user_id)} className="text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button></span>
          </div>
        ))}
        {!(group.members || []).length && <p className="text-xs text-[color:var(--ink-3)]">No members yet — add teammates above.</p>}
      </div>
    </div>
  );
}

export function AccessGroupsPanel({ users }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const load = () => api.get("/access/groups").then(({ data: d }) => {
    setData(d);
    setSelected((s) => (s ? d.groups.find((g) => g.id === s.id) || null : null));
  }).catch(() => setData({ groups: [], auto_groups: [] }));
  useEffect(() => { load(); }, []);
  const del = async (g) => {
    try { await api.delete(`/access/groups/${g.id}`); toast.success(`Group “${g.name}” deleted`); if (selected?.id === g.id) setSelected(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onChanged = (g) => { setSelected(g); load(); };
  if (!data) return <div className="py-10 text-center text-[color:var(--ink-3)]"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  return (
    <div className="space-y-4" data-testid="access-groups-panel">
      <GroupDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} initial={dlg.initial} onSaved={(g) => { load(); if (dlg.initial?.id) setSelected(g); }} />
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-[color:var(--ink-3)]">Groups make it easy to grant dashboards and datasets to whole teams. Auto groups follow workspace roles and can't be edited.</p>
        <Button data-testid="group-create-btn" onClick={() => setDlg({ open: true, initial: null })} className="bg-[color:var(--brand-ocean)] text-white shrink-0"><Plus className="w-4 h-4 mr-1" />New group</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-4">
        <div className="space-y-2">
          {data.auto_groups.map((g) => (
            <div key={g.id} data-testid={`auto-group-row-${g.id}`} className="glass-card px-4 py-3 flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[color:var(--ink)]"><Sparkles className="w-4 h-4 text-[color:var(--brand-mint)]" />{g.name}<Badge variant="outline" className="text-[9px] px-1 py-0">auto</Badge></span>
              <span className="text-xs text-[color:var(--ink-3)]">{g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
            </div>
          ))}
          {data.groups.map((g) => (
            <div key={g.id} data-testid={`group-row-${g.id}`} onClick={() => setSelected(g)}
              className={cn("glass-card px-4 py-3 flex items-center justify-between cursor-pointer transition-colors", selected?.id === g.id && "ring-2 ring-[color:var(--brand-ocean)]")}>
              <span className="flex items-center gap-2 text-sm text-[color:var(--ink)] min-w-0"><Users className="w-4 h-4 text-[color:var(--brand-ocean)] shrink-0" /><span className="truncate">{g.name}</span></span>
              <span className="flex items-center gap-1 shrink-0">
                <span className="text-xs text-[color:var(--ink-3)] mr-1">{g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                <Button data-testid={`group-rename-${g.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setDlg({ open: true, initial: g }); }}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button data-testid={`group-delete-${g.id}`} size="sm" variant="ghost" className="h-7 w-7 p-0 text-[color:var(--danger)]" onClick={(e) => { e.stopPropagation(); del(g); }}><Trash2 className="w-3.5 h-3.5" /></Button>
              </span>
            </div>
          ))}
          {!data.groups.length && <EmptyState icon={Users} title="No named groups yet" description="Create a group, add teammates, then grant it dashboards or datasets." />}
        </div>
        <div>
          {selected ? <MembersPanel group={selected} users={users} onChanged={onChanged} />
            : <div className="glass-card p-6 text-center text-sm text-[color:var(--ink-3)]" data-testid="group-members-empty">Select a group to manage its members.</div>}
        </div>
      </div>
    </div>
  );
}
